<template>
  <div id="story-board-content" class="page-no-break-before" :class="print_mode ? '' : 'vh-adjust'">
    <template v-if="disclosures === null">
      <div class="mb-5 vh-adjust">
        <div>
          <span class="mt-5"></span>
          <h5 class="mb-4 border-bottom-dark pb-2">
            <b-skeleton animation="wave" width="15%" />
            <b-skeleton animation="wave" width="30%" />
            <span class="gray-500 f-105 fw-400 ml-1">
              <b-skeleton animation="wave" width="20%" />
            </span>
          </h5>
        </div>

        <b-skeleton animation="fade" width="85%" />
        <b-skeleton animation="fade" width="55%" />
        <b-skeleton animation="fade" width="80%" />
        <b-skeleton animation="fade" width="30%" />
        <b-skeleton animation="fade" width="40%" />
        <br />
        <b-skeleton animation="fade" width="40%" />
        <b-skeleton animation="fade" width="20%" />
      </div>
    </template>

    <template v-else-if="disclosures.length > 0">
      <div
        v-for="(disclosure_group, group_idx) in groupedDisclosures"
        :id="
          !print_mode && disclosure_group.type !== 'documents'
            ? 'order_id_' + disclosure_group.order_index
            : ''
        "
        :key="'group_' + group_idx"
      >
        <!-- 그룹화된 문서 처리 -->
        <div
          v-if="disclosure_group.type === 'documents'"
          class="documents-group mt-3 no-page-break-inside text-box"
          :style="{ 'justify-content': disclosure_group.items[0].alignment }"
        >
          <DocumentSb
            v-for="(doc, doc_idx) in disclosure_group.items"
            :key="`document_${doc_idx}`"
            :print_mode="print_mode"
            :document_info="doc.data"
            :document_id="doc.data.id"
          />
        </div>

        <!-- 기타 타입 처리 -->
        <template v-else-if="!disclosure_group.guide_mode">
          <div
            v-if="disclosure_group.type === 'title'"
            class="text-box"
            :style="{ backgroundColor: disclosure_group.background_color }"
          >
            <Title
              v-if="lang === 'KOR'"
              :main_title="disclosure_group?.title1"
              :title_lv="disclosure_group?.title_lv"
              :link="disclosure_group?.link"
              :print_mode="print_mode"
              :is-first-lv1="disclosure_group.title_lv === 1 && group_idx === 0"
              :prev-title-lv="groupedDisclosures[group_idx - 1]?.title_lv || null"
            />

            <Title
              v-else
              :main_title="
                disclosure_group?.title1_em
                  ? disclosure_group?.title1_em
                  : disclosure_group?.title1_ea
              "
              :title_lv="disclosure_group?.title_lv"
              :link="disclosure_group?.link"
              :print_mode="print_mode"
              :is-first-lv1="disclosure_group.title_lv === 1 && group_idx === 0"
              :prev-title-lv="groupedDisclosures[group_idx - 1]?.title_lv || null"
            />
          </div>

          <div
            v-else-if="disclosure_group.type === 'text'"
            class="mt-0 text-box"
            :style="{ backgroundColor: disclosure_group.background_color }"
          >
            <mdsb v-if="lang === 'KOR'" :contents="disclosure_group.text" />

            <mdsb
              v-else
              :contents="
                disclosure_group.text_em ? disclosure_group.text_em : disclosure_group.text_ea
              "
            />
          </div>

          <div
            v-else-if="disclosure_group.type === 'image'"
            class="d-flex flex-column mb-3 no-page-break-inside text-box"
            :style="{ backgroundColor: disclosure_group.background_color }"
          >
            <ImageContent
              :disclosure-info="disclosure_group"
              :lang="lang"
              :show_title="disclosure_group.show_title"
              @image-loaded="onImageLoaded"
            />
          </div>

          <div v-else-if="disclosure_group.type === 'line'">
            <hr :style="disclosure_group.hr_style" />
          </div>

          <div
            v-else-if="disclosure_group.type === 'data_table'"
            class="mb-3 no-page-break-inside text-box"
            :style="{ backgroundColor: disclosure_group.background_color }"
          >
            <DataTableSb
              :data_table_data="disclosure_group.data"
              :print_mode="print_mode"
              table_mode="accordion"
              :show_title="disclosure_group.show_title"
              :force_lang="lang"
            />
          </div>

          <div
            v-else-if="disclosure_group.type === 'chart'"
            class="mb-3 no-page-break-inside text-box"
            :style="{ backgroundColor: disclosure_group.background_color }"
          >
            <ChartCanvasSb
              :key="`chart_${disclosure_group.data.id}`"
              :chart_id="disclosure_group.data.id"
              :chart-data="disclosure_group.data.contents"
              :chart-data-e="disclosure_group.data.translated?.contents_e"
              :print_mode="print_mode"
            />
          </div>

          <div
            v-else-if="disclosure_group.type === 'business_case'"
            class="mb-3 no-page-break-inside text-box"
          >
            <BusinessCaseSb
              :business_case_obj="disclosure_group.data"
              :business_case_id="disclosure_group.data.id"
              business_case_mode="accordion"
              :show_title="disclosure_group.show_title"
              :background_color="disclosure_group.background_color"
            />
          </div>

          <div
            v-else-if="disclosure_group.type === 'document'"
            class="my-3 text-box"
            :style="{ 'justify-content': disclosure_group.items[0].alignment }"
          >
            <DocumentSb
              :document_info="disclosure_group.data"
              :document_id="disclosure_group.data.id"
            />
          </div>

          <div v-else-if="disclosure_group.type === 'document_set'" class="my-3 text-box">
            <DocumentSetSb :disclosure_group="disclosure_group" />
          </div>
        </template>
      </div>
    </template>

    <template v-else>
      <p>작성된 게시물이 없습니다.</p>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import DocumentSb from './DocumentSb.vue';
import DocumentSetSb from './DocumentSetSb.vue';
import DataTableSb from './DataTableSb.vue';
import BusinessCaseSb from './BusinessCaseSb.vue';
import ChartCanvasSb from './ChartCanvasSb.vue';
import Title from './Title.vue';
import ImageContent from './ImageContent.vue';

export default {
  name: 'Content',
  components: {
    DocumentSb,
    DataTableSb,
    BusinessCaseSb,
    ChartCanvasSb,
    Title,
    ImageContent,
    DocumentSetSb
  },
  props: {
    disclosures: { type: Array, default: null },
    print_mode: { type: Boolean, default: false }
  },
  watch: {
    disclosures: {
      immediate: true,
      handler(newV, oldV) {
        if (newV && JSON.stringify(newV) !== JSON.stringify(oldV)) {
          this.countImages();
          this.$nextTick(() => {
            this.moveScroll();
            setTimeout(() => {
              this.moveScroll(); // 즉시 스크롤  + 500ms 후 재시도로 SVG 렌더링 완료 후 정확한 위치로 보정
            }, 500);
          });
        }
      }
    }
  },
  computed: {
    ...mapState('report_list', ['lang']),

    // 새로운 계산된 속성: groupedDisclosures
    groupedDisclosures() {
      if (!this.disclosures) return [];

      const grouped = [];
      let currentGroup = [];

      this.disclosures.forEach((item, index) => {
        if (item.type === 'document') {
          currentGroup.push({ ...item, index });
        } else {
          if (currentGroup.length > 0) {
            grouped.push({ type: 'documents', items: [...currentGroup] });
            currentGroup = [];
          }
          grouped.push({ ...item, index });
        }
      });

      if (currentGroup.length > 0) {
        grouped.push({ type: 'documents', items: [...currentGroup] });
      }

      return grouped;
    }
  },
  data() {
    return {
      imageLoadCount: 0,
      totalImageCount: 0,
      scrollAttempted: false
    };
  },
  methods: {
    moveScroll() {
      const hash = this.$route.hash;
      if (hash) {
        const targetElement = document.querySelector(hash);
        if (targetElement) {
          const elementPosition = targetElement.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - 90;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'auto'
          });
          this.scrollAttempted = true;
        } else {
          setTimeout(() => {
            this.moveScroll();
          }, 300);
        }
      }
    },
    onImageLoaded() {
      this.imageLoadCount++;
      if (this.imageLoadCount >= this.totalImageCount && this.scrollAttempted) {
        setTimeout(() => {
          this.moveScroll();
        }, 100);
      }
    },
    countImages() {
      this.totalImageCount = this.disclosures?.filter(item => item.type === 'image').length || 0;
      this.imageLoadCount = 0;
      this.scrollAttempted = false;
    }
  }
};
</script>

<style>
.documents-group {
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 16px;
  /* 문서 간 간격 조정 */
}
</style>
